import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import { Role } from "../../stores/auth";
import { useInspectionsStore } from "../../stores/inspections";
import { useUserStore } from "../../stores/users";
import Modal, { ModalBody, ModalFooter, ModalHeader } from "./Modal";

const STEP = Object.freeze({
  REVIEW: "review",
  RUNNING: "running",
  DONE: "done",
});

export default function ConvertInspectionsWizard({ inspections, onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { convertToWorkOrder } = useInspectionsStore(
    useShallow((state) => ({ convertToWorkOrder: state.convertToWorkOrder })),
  );

  const {
    byIds: usersByIds,
    allIds: usersAllIds,
    fetchSome: fetchUsers,
  } = useUserStore(
    useShallow((state) => ({
      byIds: state.byIds,
      allIds: state.allIds,
      fetchSome: state.fetchSome,
    })),
  );

  const [step, setStep] = useState(STEP.REVIEW);
  const [results, setResults] = useState({});
  // User-picked assignee overrides ("" = unassigned); rows without an entry
  // default to the technician matched by inspector number.
  const [assignments, setAssignments] = useState({});

  useEffect(() => {
    fetchUsers({ limit: 100, offset: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const technicians = useMemo(
    () =>
      usersAllIds
        .map((id) => usersByIds[id])
        .filter((u) => u?.roles?.some((r) => r.name === Role.TECHNICIAN)),
    [usersByIds, usersAllIds],
  );

  const rows = inspections.map((item) => {
    const inspectorNumber = item.inspector_number || item.inspector;
    const matched = inspectorNumber
      ? technicians.find((u) => u.number === inspectorNumber) || null
      : null;
    const assigneeId =
      assignments[item._id] !== undefined
        ? assignments[item._id]
        : matched
          ? String(matched.id)
          : "";
    const technician = assigneeId
      ? technicians.find((u) => String(u.id) === assigneeId) || null
      : null;
    return { item, technician, assigneeId, result: results[item._id] };
  });

  const isRunning = step === STEP.RUNNING;
  const isReview = step === STEP.REVIEW;
  const isSingle = inspections.length === 1;
  const confirmMessage = isSingle
    ? t("inspections.confirm_convert")
    : t("inspections.confirm_convert_many", { count: inspections.length });
  const convertLabel = isSingle
    ? t("inspections.convert_short")
    : `${t("inspections.convert_short")} (${inspections.length})`;

  const errorMessage = (errorKey) => {
    if (errorKey) {
      const translated = t(errorKey);
      if (translated !== errorKey) return translated;
    }
    return t("errors.unknown");
  };

  const handleConvert = async () => {
    setStep(STEP.RUNNING);
    let successCount = 0;
    let firstWorkOrderId = null;
    for (const { item, assigneeId } of rows) {
      setResults((prev) => ({ ...prev, [item._id]: { status: "running" } }));
      const result = await convertToWorkOrder(item._id, {
        assignedToId: assigneeId ? Number(assigneeId) : null,
      });
      if (result.data) {
        successCount += 1;
        if (firstWorkOrderId === null) firstWorkOrderId = result.data.id;
        setResults((prev) => ({
          ...prev,
          [item._id]: { status: "done", workOrderId: result.data.id },
        }));
      } else {
        setResults((prev) => ({
          ...prev,
          [item._id]: {
            status: "error",
            errorKey: result.error?.body?.detail?.[0]?.t,
          },
        }));
      }
    }
    if (isSingle && successCount === 1) {
      navigate(`/work_orders/${firstWorkOrderId}/details`);
      return;
    }
    setStep(STEP.DONE);
  };

  return (
    <Modal size="xl">
      <ModalHeader>
        <h5 className="modal-title">{t("inspections.convert")}</h5>
      </ModalHeader>
      <ModalBody>
        {isReview && <p>{confirmMessage}</p>}
        <div className="table-responsive">
          <table className="table table-hover table-panel align-middle mb-0">
            <thead>
              <tr>
                <th>{t("inspections.customer")}</th>
                <th className="text-nowrap">{t("inspections.device_type")}</th>
                <th className="text-nowrap">{t("inspections.next_inspection")}</th>
                <th className="text-nowrap">{t("work_orders.assigned_to")}</th>
                {!isReview && <th className="text-nowrap">{t("work_orders.status")}</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map(({ item, technician, assigneeId, result }) => (
                <tr key={item._id}>
                  <td>
                    {item.customer_name && <div>{item.customer_name}</div>}
                    {item.property_name && (
                      <div className="text-muted small">{item.property_name}</div>
                    )}
                  </td>
                  <td className="text-nowrap">{item.device_type || "-"}</td>
                  <td className="text-nowrap">{item.next_inspection || "-"}</td>
                  <td className="text-nowrap">
                    {isReview ? (
                      <select
                        className="form-select"
                        aria-label={t("work_orders.assigned_to")}
                        value={assigneeId}
                        onChange={(e) =>
                          setAssignments((prev) => ({
                            ...prev,
                            [item._id]: e.target.value,
                          }))
                        }
                      >
                        <option value="">{t("work_orders.unassigned")}</option>
                        {technicians.map((tech) => (
                          <option key={tech.id} value={tech.id}>
                            {tech.name}
                            {tech.number ? ` (${tech.number})` : ""}
                          </option>
                        ))}
                      </select>
                    ) : technician ? (
                      technician.name
                    ) : (
                      <span className="text-muted">{t("work_orders.unassigned")}</span>
                    )}
                  </td>
                  {!isReview && (
                    <td className="text-nowrap">
                      {result?.status === "running" && (
                        <i className="fa fa-spinner fa-spin" />
                      )}
                      {result?.status === "done" && (
                        <Link
                          to={`/work_orders/${result.workOrderId}/details`}
                          className="badge badge-link bg-default text-dark"
                          title={t("inspections.open_work_order")}
                        >
                          <i className="fa fa-clipboard-list fa-fw me-1" />
                          {t("inspections.converted")}
                        </Link>
                      )}
                      {result?.status === "error" && (
                        <span className="text-danger">
                          {errorMessage(result.errorKey)}
                        </span>
                      )}
                      {!result && <span className="text-muted">-</span>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ModalBody>
      <ModalFooter>
        {step === STEP.DONE ? (
          <button type="button" className="btn btn-default" onClick={onClose}>
            {t("common.close")}
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-default"
              onClick={onClose}
              disabled={isRunning}
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              className="btn btn-success"
              onClick={handleConvert}
              disabled={isRunning}
            >
              {isRunning && <i className="fa fa-spinner fa-spin me-1" />}
              {convertLabel}
            </button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
}

ConvertInspectionsWizard.propTypes = {
  inspections: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
};
